/**
 * Round 8 B2 -- the classification decision on the service's own page.
 *
 * C1 put role, person, timestamp and comment on five records of six.
 * The one it missed is the page a service owner's classification
 * ceremony deep-links to from My Work: the service page printed the
 * seeded description, live state, findings and obligations, and said
 * nothing about the proposal he had just signed, the rationale he was
 * asked to agree with, or the signatures now on it. The ceremony left
 * no trace on the record it is about.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, type ApiError } from "shared/api/client";
import { Card } from "shared/components/Card";
import { termLabel } from "shared/vocab/governance";
import { SignatureHistory } from "shared/components/SignatureHistory";
import { formatDate } from "shared/time/formatDate";

export interface ServiceClassification {
  id: string;
  classification: string;
  rationale: string;
  evaluator_name: string | null;
  decided_at: string;
  reviewed_at: string | null;
  next_review_due: string | null;
  workflow_state: string;
  cro_signoff_name: string | null;
  cro_signoff_at: string | null;
  cro_signoff_note: string | null;
  service_owner_signoff_name: string | null;
  service_owner_signoff_at: string | null;
  service_owner_signoff_note: string | null;
}

export function ServiceClassificationPanel({ serviceId }: { serviceId: string }) {
  const { t, i18n } = useTranslation();
  const decision = useQuery<ServiceClassification | null, ApiError>({
    queryKey: ["service-classification", serviceId] as const,
    queryFn: () =>
      api.get<ServiceClassification | null>(
        `/v1/twin/services/${serviceId}/classification`,
      ),
    // A service classified by nobody yet is an ordinary state, not an
    // error worth retrying.
    retry: false,
  });

  if (decision.isLoading || decision.isError) return null;
  const d = decision.data;
  if (!d) {
    return (
      <Card
        testid="service-classification-none"
        micro={t("screens.serviceClassificationPanel.heading")}
      >
        <p className="text-sm text-slate-600">
          {t("screens.serviceClassificationPanel.notYetClassified")}
        </p>
      </Card>
    );
  }

  return (
    <Card
      testid="service-classification-panel"
      micro={t("screens.serviceClassificationPanel.heading")}
    >
      <div className="space-y-2">
        <p className="text-sm text-slate-800">
          {/* Round 10 C4 -- the words he SIGNED, not the column value.
              The sign panel put "Supports an important business
              service" in front of him; the page explaining the decision
              printed "IBS supporting", which is the enum. */}
          <span className="font-medium" data-testid="service-classification-label">
            {termLabel(d.classification, "myWork.summary.classification")}
          </span>
          {/* ...and a separator in the TEXT, not only in the margin.
              Fourth round for the space. Round 10 put a "·" in the
              text and no space BEFORE it, so innerText went from
              "IBS supportingdecided" to "service· decided" -- still
              one word, still what a copy into a board pack and a
              screen reader get, and the fix's own comment said this
              was what it was for. */}
          <span className="ms-2 text-xs text-slate-500">
            {" · "}
            {t("screens.serviceClassificationPanel.decidedOn", {
              when: formatDate(d.decided_at, i18n.language),
            })}
            {d.evaluator_name ? ` · ${d.evaluator_name}` : ""}
          </span>
        </p>
        {/* The rationale is the whole point of the ceremony: the reason
            a service was classified the way it was lived only in the
            database on the one page that names the service. */}
        <p
          data-testid="service-classification-rationale"
          className="max-w-prose text-sm text-slate-700"
        >
          {d.rationale}
        </p>
        {d.next_review_due && (
          <p className="text-xs text-slate-500">
            {t("screens.serviceClassificationPanel.nextReview", {
              when: formatDate(d.next_review_due, i18n.language),
            })}
          </p>
        )}
        <SignatureHistory
          testid="service-classification-signatures"
          entries={[
            {
              roleLabel: t("roles.cro"),
              name: d.cro_signoff_name,
              at: d.cro_signoff_at,
              note: d.cro_signoff_note,
              // Round 10 F2 -- the sign modal asks for a name and an
              // email. There is no box, so an empty note says nothing
              // about the signer.
              commentOffered: false,
            },
            {
              roleLabel: t("roles.service_owner"),
              name: d.service_owner_signoff_name,
              at: d.service_owner_signoff_at,
              note: d.service_owner_signoff_note,
              commentOffered: false,
            },
          ]}
        />
      </div>
    </Card>
  );
}
